import { ceil } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { useRef, useState } from 'react';
import { getOperateType, useMoveEventListener, type IFramework } from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { isImageOutOfRangeLayer } from '@/utils/image-layer-crop';
import { BaseLayerViewType, LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';

export const useScaleImageOperateRealTime = (framework: IFramework) => {
    const [originScale, setOriginScale] = useState(0);
    const [moveX, setMoveX] = useState(0);
    const originLayerRef = useRef<ILayerProps | undefined>(undefined);
    const originLayer = useRef<ILayerProps | undefined>();
    useMoveEventListener((e: ITouchKitEvent) => {
        const layer = framework.service.frame.getActivePickLayer();
        if (!layer || layer?.layerType !== LayerType.IMAGE_LAYER) return null;

        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');

        if (!getOperateType(target)) return;

        if (dataType !== 'imgScale') return;

        const { imageTransform } = layer.data as IImageLayerProps;
        const { scale } = imageTransform;

        if (e.isStart) {
            originLayer.current = cloneDeep(layer);
            originLayerRef.current = cloneDeep(layer);
            setOriginScale(scale);
            framework.service.frame.addOrUpdateLayer({
                ...cloneDeep(layer),
                __isBasicOperating: true,
            });
            return;
        }

        let result = 1;
        const { displacementX } = e;

        const moveX = displacementX;

        if (moveX > 0) {
            result = originScale + moveX * ((3 - originScale) / 50);
        } else {
            result = originScale + moveX * (originScale / 25);
        }

        if (result < 1) {
            result = 1;
        } else if (result > 3) {
            result = 3;
        }
        imageTransform.scale = result;

        setMoveX(moveX);

        framework.service.editor.setIsBaseOperate(true);

        const isOutAndScaleLayer = (outLayer: ILayerProps) => {
            const isOut = isImageOutOfRangeLayer(outLayer);
            // 超出了就加scale
            // 直到不超出为止
            // 如果是顺时针旋转超出了

            // 在初始角度和上一个角度之间就判断为往回走 不然就是往前走
            const { width, height, data } = outLayer;
            const { imageTransform } = data as IImageLayerProps;
            const { offsetX, offsetY, widthRatio, heightRatio, scale: imageScale } = imageTransform;
            const imageWidth = width * widthRatio * imageScale;
            const imageHeight = height * heightRatio * imageScale;
            const imageOffsetX = offsetX * widthRatio * width * imageScale;
            const imageOffsetY = offsetY * heightRatio * height * imageScale;
            if (isOut) {
                const newImageWidth =
                    imageWidth > imageHeight ? imageWidth + 1 : ceil((imageHeight + 1) / (imageHeight / imageWidth));
                const newImageHeight =
                    imageHeight > imageWidth ? imageHeight + 1 : ceil((imageWidth + 1) / (imageWidth / imageHeight));
                const newImageScale = newImageWidth / (width * widthRatio);
                const newOffsetX = imageOffsetX / newImageWidth;
                const newOffsetY = imageOffsetY / newImageHeight;
                imageTransform.scale = newImageScale;
                imageTransform.offsetX = newOffsetX;
                imageTransform.offsetY = newOffsetY;
                isOutAndScaleLayer(outLayer);
            }
        };
        isOutAndScaleLayer(layer);
        layer.__isBasicOperating = e.isEnd ? undefined : true;
        framework.service.frame.addOrUpdateLayer(cloneDeep(layer));
        framework.service.frame.setActiveLayer(layer.id);

        if (e.isEnd) {
            if (originLayerRef.current) {
                framework.service.history.write(originLayerRef.current);
            }

            framework.service.frame.setActiveLayer(layer.id);

            setMoveX(0);
        }
        framework.service.frame.__emitBaseOperate(layer.id, BaseLayerViewType.IMAGE_SCALE, {
            isStart: e.isStart,
            isEnd: e.isEnd,
            oldLayer: originLayer.current,
        });

        if (e.isEnd) {
            framework.service.editor.setIsBaseOperate(false);
            originLayer.current = undefined;
        }
    });

    return {
        moveX,
    };
};
